import { supabase } from './supabase'
import type { LoyaltyCard } from '../types'

/**
 * Müşteri sadakat (30 Eki 2026, supabase/sadakat.sql). Kart yazmaları sunucu
 * fonksiyonlarından, kasa yetkisiyle; damga sayısını kurala göre sunucu verir.
 * Çevrimdışı çalışmaz: kart kodu sorulduğunda bağlantı gerekir.
 */

const toCard = (r: Record<string, unknown>): LoyaltyCard => ({
  id: String(r.id),
  code: String(r.code),
  token: String(r.token),
  name: String(r.name ?? ''),
  phone: r.phone ? String(r.phone) : null,
  stamps: Number(r.stamps ?? 0),
  rewardsUsed: Number(r.rewards_used ?? 0),
})

function client() {
  if (!supabase) throw new Error('Bağlantı yok.')
  return supabase
}

export async function createLoyaltyCard(tenantId: string, name: string, phone: string): Promise<LoyaltyCard> {
  const { data, error } = await client().rpc('loyalty_create', { t: tenantId, p_name: name, p_phone: phone })
  if (error?.message.includes('LOYALTY_PHONE_EXISTS'))
    throw new Error('Bu telefonla açılmış bir kart var. "Kart bul"dan telefonla arayın.')
  if (error || !data) throw new Error('Kart açılamadı. İnternet bağlantınızı kontrol edin.')
  return toCard(data as Record<string, unknown>)
}

/** Aramada en çok bu kadar kart listelenir; sunucu bir fazlasını verirse "daha fazlası var". */
export const LOYALTY_SEARCH_LIMIT = 20

/** Kod, telefon (ya da parçası) veya ad soyadla (supabase/sadakat-arama.sql). */
export async function searchLoyaltyCards(tenantId: string, q: string): Promise<{ cards: LoyaltyCard[]; more: boolean }> {
  const { data, error } = await client().rpc('loyalty_search', { t: tenantId, p_q: q })
  if (error) throw new Error('Kart aranamadı. İnternet bağlantınızı kontrol edin.')
  const rows = ((data as Record<string, unknown>[] | null) ?? []).map(toCard)
  return { cards: rows.slice(0, LOYALTY_SEARCH_LIMIT), more: rows.length > LOYALTY_SEARCH_LIMIT }
}

/** Satış kapandı: ödül kullanıldıysa düşer, kurala göre damga eklenir. */
export async function loyaltyAfterSale(
  tenantId: string,
  code: string,
  orderId: string,
  totalKurus: number,
  rewardUsed: boolean,
): Promise<LoyaltyCard> {
  const { data, error } = await client().rpc('loyalty_after_sale', {
    t: tenantId,
    p_code: code,
    p_order: orderId,
    p_total: totalKurus,
    p_reward: rewardUsed,
  })
  if (error || !data) throw new Error('Damga eklenemedi.')
  return toCard(data as Record<string, unknown>)
}

/** Müşterinin kart sayfası (girişsiz). */
export interface PublicLoyaltyCard {
  business: string
  tenant: string
  code: string
  name: string
  stamps: number
  target: number
  rewardPercent: number
  per: 'visit' | 'amount'
  amountKurus: number
  rewards: number
}

export async function fetchPublicCard(token: string): Promise<PublicLoyaltyCard | null> {
  const { data, error } = await client().rpc('loyalty_card_public', { p_token: token })
  if (error) throw new Error('Kart açılamadı.')
  return (data as PublicLoyaltyCard | null) ?? null
}

/** Müşterinin kart sayfasının adresi (kasada karekod olarak gösterilir). */
export const cardUrl = (token: string) => `https://adissoo.com/k/${token}`
